/**
 * Sites in the composer (plan 7.10, the user's `create-composer-sites-chip.png`).
 *
 * - Sites mode: the Sites list's Create opens a new chat with a removable
 *   "Sites" chip at the start of the input and the placeholder "Create a
 *   website that ...". The message sent in Sites mode asks for a page (the
 *   Page result), and when its `createPage` result arrives the page gets a
 *   draft site (`POST /api/sites/drafts`) bound to the thread, so the site is
 *   in the Sites list at once.
 * - Thread bindings: a thread bound to a site (made in Sites mode, or opened
 *   with Edit from the list) sends the site's page as `artifactContext` with
 *   `site: true` on every message, so the agent edits that page instead of
 *   making a new file.
 * - A one-shot context: Edit on a site whose thread is gone, and Fix with
 *   Tabbio, arm the page for the next message of a new chat.
 *
 * Module state like the composer preferences, because the Sites screens, the
 * composers and the chat runtime (memoized per thread, outside React) all
 * read it.
 */
import { useSyncExternalStore } from 'react';
import type { ArtifactContext, PublishedSiteSummary } from '@tabbio/types';
import { consumeArtifactAskContext } from './artifact-ask';

/**
 * `artifactContext` as the client sends it. `site` marks a page that is (or
 * is becoming) a site; an API that does not read it drops it, since the
 * context is parsed without the strict flag.
 */
export type SiteArtifactContext = ArtifactContext & { site?: boolean };

export type SiteBinding = { siteId: string; artifactId: string };

// ── Sites mode ─────────────────────────────────────────────────────────────

let siteMode = false;
const modeListeners = new Set<() => void>();

function notifyMode() {
  for (const listener of modeListeners) listener();
}

/** Create on the Sites list: the next new chat opens in Sites mode. */
export function startSiteCompose() {
  if (siteMode) return;
  siteMode = true;
  notifyMode();
}

/** The chip's remove button, or leaving the flow. */
export function clearSiteCompose() {
  if (!siteMode) return;
  siteMode = false;
  notifyMode();
}

export function readSiteCompose(): boolean {
  return siteMode;
}

/** Read and clear at send time: one message carries Sites mode. */
export function consumeSiteCompose(): boolean {
  if (!siteMode) return false;
  siteMode = false;
  notifyMode();
  return true;
}

function subscribeSiteCompose(listener: () => void) {
  modeListeners.add(listener);
  return () => {
    modeListeners.delete(listener);
  };
}

export function useSiteComposeMode(): boolean {
  return useSyncExternalStore(subscribeSiteCompose, readSiteCompose, () => false);
}

// ── Thread bindings ────────────────────────────────────────────────────────

const bindings = new Map<string, SiteBinding>();
let pendingContext: SiteArtifactContext | null = null;

export function bindThreadToSite(threadId: string, binding: SiteBinding) {
  if (!threadId || !binding.artifactId) return;
  bindings.set(threadId, binding);
}

export function readThreadSiteBinding(threadId: string | null | undefined): SiteBinding | null {
  return threadId ? bindings.get(threadId) ?? null : null;
}

/** The Sites list knows every site's thread: remember them for later messages. */
export function rememberSiteBindings(
  sites: readonly Pick<PublishedSiteSummary, 'id' | 'artifactId' | 'threadId'>[],
) {
  for (const site of sites) {
    if (site.threadId) bindings.set(site.threadId, { siteId: site.id, artifactId: site.artifactId });
  }
}

/** Forget a deleted site's thread. */
export function forgetSiteBinding(siteId: string) {
  for (const [threadId, binding] of bindings) {
    if (binding.siteId === siteId) bindings.delete(threadId);
  }
}

/** The next message of a new chat carries this page (Edit without a thread, Fix with Tabbio). */
export function armArtifactContext(context: SiteArtifactContext) {
  pendingContext = context;
}

/**
 * What one message carries, read at send time: a selection from the canvas
 * (Ask Tabbio or a quick action, plan 7.4; marked as the site when it is the
 * thread's site page), else the thread's site, else the one-shot context
 * (consumed), else nothing.
 */
export function artifactContextForSend(
  threadId: string | null | undefined,
): SiteArtifactContext | undefined {
  const binding = readThreadSiteBinding(threadId);
  const asked = consumeArtifactAskContext();
  if (asked) return binding?.artifactId === asked.artifactId ? { ...asked, site: true } : asked;
  if (binding) return { artifactId: binding.artifactId, site: true };
  if (pendingContext && !threadId) {
    const context = pendingContext;
    pendingContext = null;
    return context;
  }
  return undefined;
}

// ── Draft sites from the Sites composer ────────────────────────────────────

type DraftDeps = {
  createDraft: (body: { artifactId: string; threadId?: string }) => Promise<{
    id: string;
    artifactId: string;
    threadId: string | null;
  }>;
  /** Refresh the Sites list and the artifact's site after a draft is made. */
  onCreated?: (binding: SiteBinding) => void;
};

const draftedArtifacts = new Set<string>();

/**
 * The `createPage` result of a Sites-mode message: the page becomes a draft
 * site bound to the thread (idempotent per artifact, server side too). A
 * failure only means the page is not in the Sites list yet; it never breaks
 * the chat.
 */
export async function createDraftForSitePage(
  input: { artifactId: string; threadId: string | undefined },
  deps: DraftDeps,
): Promise<SiteBinding | null> {
  if (!input.artifactId || draftedArtifacts.has(input.artifactId)) return null;
  draftedArtifacts.add(input.artifactId);
  try {
    const site = await deps.createDraft({
      artifactId: input.artifactId,
      ...(input.threadId ? { threadId: input.threadId } : {}),
    });
    const binding = { siteId: site.id, artifactId: site.artifactId };
    const threadId = site.threadId ?? input.threadId;
    if (threadId) bindThreadToSite(threadId, binding);
    deps.onCreated?.(binding);
    return binding;
  } catch {
    draftedArtifacts.delete(input.artifactId);
    return null;
  }
}

/** The artifact id of a `createPage` tool result, or null. */
export function sitePageArtifactId(toolName: string, result: unknown): string | null {
  if (toolName !== 'createPage') return null;
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null;
  const id = (result as { artifactId?: unknown }).artifactId;
  return typeof id === 'string' && id ? id : null;
}

export function resetSiteComposeForTests() {
  siteMode = false;
  modeListeners.clear();
  bindings.clear();
  pendingContext = null;
  draftedArtifacts.clear();
}
